import {ApiClient} from '@hconnect/apiclient'
import type {AxiosRequestConfig} from 'axios'

/**
 * Domain (host) overrides for groups of endpoints, owned by Suite.
 *
 * Some endpoint groups can be redirected to a different backend host at runtime, driven
 * by a feature flag (e.g. `useHproduceDomain` redirects the `janus` group to the
 * dedicated HProduce domain). The override only swaps the request host, the request
 * path and query stay untouched.
 *
 * This is implemented entirely on top of the apiclient public API (a request
 * interceptor registered on `ApiClient.axiosInstance`) so the shared apiclient package
 * is never modified.
 *
 * To add a new redirectable group:
 *  1. Add a member to the `EndpointGroup` union.
 *  2. Add a matching entry to `endpointGroups`.
 */
export type EndpointGroup = 'janus'

interface EndpointGroupDefinition {
  group: EndpointGroup
  /** Matched against the request `url` (path). First matching group wins. */
  matchers: RegExp[]
}

/**
 * Single source of truth describing which request paths belong to which group.
 * Only the dedicated `janus-*` services are considered part of the `janus` group;
 * shared services (stoppages, shifthandover, upm, kms, asset-config, ...) are left
 * on the default backend.
 */
const endpointGroups: EndpointGroupDefinition[] = [
  {
    group: 'janus',
    matchers: [
      /(^|\/)janus-daily-kpi\//,
      /(^|\/)janus-kpi-events\//,
      /(^|\/)janus-monthly-result\//,
      /(^|\/)janus-pss\//,
      /(^|\/)janus-kpi\//,
      /(^|\/)janus-kpi-validation\//
    ]
  }
]

let activeOverrides: ReadonlyMap<EndpointGroup, string> = new Map()

/**
 * Replaces the set of active overrides. Pass an empty map to clear all overrides.
 * Each entry maps a group to the base URL its requests should be redirected to.
 */
export function setDomainOverrides(overrides: ReadonlyMap<EndpointGroup, string>): void {
  activeOverrides = overrides
}

/**
 * Returns the override base URL for the given request url, or `undefined` when the
 * url does not belong to any actively-overridden group.
 */
export function getDomainOverrideForUrl(url?: string): string | undefined {
  if (!url || activeOverrides.size === 0) return undefined
  const definition = endpointGroups.find((group) =>
    group.matchers.some((matcher) => matcher.test(url))
  )
  return definition ? activeOverrides.get(definition.group) : undefined
}

/**
 * Request interceptor that redirects requests of an actively-overridden endpoint group
 * to a different host.
 *
 * The override is applied by turning the request `url` into an absolute URL and
 * clearing `baseURL`. Axios ignores `baseURL` when `url` is absolute, so the override
 * survives regardless of interceptor ordering — no other interceptor that sets
 * `baseURL` (e.g. apiclient's base-url interceptor) can route the request to the wrong
 * host.
 */
export const domainOverrideInterceptor = (config: AxiosRequestConfig): AxiosRequestConfig => {
  const override = getDomainOverrideForUrl(config.url)
  if (!override || !config.url) return config

  // `config.url` is normally a path (optionally with a query string), but an earlier
  // interceptor or caller may already have made it absolute on the default host. In
  // both cases we resolve only the path + query against the override base, so the
  // request always lands on the target host with its path and query preserved.
  let pathWithQuery = config.url
  if (/^https?:\/\//i.test(config.url)) {
    const {pathname, search} = new URL(config.url)
    pathWithQuery = `${pathname}${search}`
  }

  const absoluteUrl = new URL(pathWithQuery, override).toString()
  return {...config, url: absoluteUrl, baseURL: undefined}
}

let isInterceptorRegistered = false

/**
 * Registers the domain-override interceptor on the shared apiclient axios instance.
 * Idempotent: registers at most once for the lifetime of the app.
 */
export function registerDomainOverrideInterceptor(): void {
  if (isInterceptorRegistered) return
  ApiClient.axiosInstance.interceptors.request.use(domainOverrideInterceptor)
  isInterceptorRegistered = true
}
